import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { closeModal } from '../../actions/modal';
import { Input } from '../../containers/Form';
import Wizard from '../../containers/Modal/Wizard';
import Strings from '../../Strings';
import '../UpdateTask/TaskDuration.scss';
import './CompleteMonitoringModal.scss';

export const COMPLETE_MONITORING_MODAL_NAME = 'complete-monitoring-modal';

// Mirrors the backend limit on /portal/patients/multiple/last-readings-archive (same as for added tasks)
const MAX_DURATION_SEC = 24 * 60 * 60;

// Same rules and messages as the duration of a task added in the Add Task modal
export const getDurationErrors = (durationMinutes, durationSeconds) => {
  const errors = [];
  const minutesValid =
    durationMinutes !== '' && Number.isSafeInteger(Number(durationMinutes)) && Number(durationMinutes) >= 0;
  const secondsValid =
    durationSeconds !== '' &&
    Number.isInteger(Number(durationSeconds)) &&
    Number(durationSeconds) >= 0 &&
    Number(durationSeconds) <= 59;
  if (!minutesValid) {
    errors.push({ property: 'durationMinutes', missing: durationMinutes === '', errors: [Strings.errors.taskDurationMinutes] });
  }
  if (!secondsValid) {
    errors.push({ property: 'durationSeconds', missing: durationSeconds === '', errors: [Strings.errors.taskDurationSeconds] });
  }
  if (minutesValid && secondsValid) {
    const duration = Number(durationMinutes) * 60 + Number(durationSeconds);
    if (duration > MAX_DURATION_SEC) {
      errors.push({ property: 'duration', missing: false, errors: [Strings.errors.taskDurationTooLong] });
    } else if (duration < 1) {
      errors.push({ property: 'duration', missing: false, errors: [Strings.errors.taskDurationRequired] });
    }
  }
  return errors;
};

const formatCount = (count, one, many) => (count === 1 ? one : Strings.formatString(many, count));

export function CompleteMonitoringModal({ name, data, onCancel }) {
  const [durationMinutes, setDurationMinutes] = useState('');
  const [durationSeconds, setDurationSeconds] = useState('00');
  const [highlightInvalidFields, setHighlightInvalidFields] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const patientsCount = data?.patientsCount || 0;
  const validationErrors = getDurationErrors(durationMinutes, durationSeconds);
  // Missing fields are marked by the required highlight alone, so only real errors get a tooltip
  const errorsFor = property =>
    validationErrors
      .filter(v => (v.property === property || v.property === 'duration') && !v.missing)
      .flatMap(v => v.errors);
  const duration = Number(durationMinutes) * 60 + Number(durationSeconds);

  // the modal stays open until the requests finish, so the list can't be used with stale data
  const onSave = async () => {
    if (isSaving || validationErrors.length > 0) {
      return;
    }
    setIsSaving(true);
    try {
      await data?.onConfirm?.(duration);
    } finally {
      setIsSaving(false);
      onCancel();
    }
  };

  const mmss = `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`;
  const timeHint =
    validationErrors.length === 0 &&
    (patientsCount > 1
      ? Strings.formatString(Strings.completeMonitoringTimeForEachPatient, mmss, patientsCount)
      : Strings.formatString(Strings.completeMonitoringTimeForPatient, mmss));

  const pageContent = (
    <>
      <p className="complete-monitoring-description">
        {Strings.formatString(
          Strings.completeMonitoringDescription,
          formatCount(patientsCount, Strings.onePatient, Strings.xPatients),
        )}
      </p>
      <fieldset className="task-duration-field">
        <legend className="required-asterisk">{Strings.capPatient.duration}</legend>
        <div className="task-duration-control">
          <Input
            id="durationMinutes"
            type="number"
            min={0}
            step={1}
            value={durationMinutes}
            onChange={e => setDurationMinutes(e.target.value)}
            label={Strings.capPatient.durationMinutes}
            className="task-duration-input"
            placeholder="00"
            onBlur={() => setDurationMinutes(value => (value === '' ? value : value.padStart(2, '0')))}
            isRequired
            errorsForTooltip={errorsFor('durationMinutes')}
            highlightInvalid={highlightInvalidFields}
            data-testid="complete-monitoring-minutes"
          />
          <Input
            id="durationSeconds"
            type="number"
            min={0}
            max={59}
            step={1}
            value={durationSeconds}
            onChange={e => setDurationSeconds(e.target.value)}
            label={Strings.capPatient.durationSeconds}
            className="task-duration-input"
            placeholder="00"
            onBlur={() => setDurationSeconds(value => (value === '' ? value : value.padStart(2, '0')))}
            isRequired
            errorsForTooltip={errorsFor('durationSeconds')}
            highlightInvalid={highlightInvalidFields}
            data-testid="complete-monitoring-seconds"
          />
        </div>
      </fieldset>
      <div className="complete-monitoring-hint" aria-live="polite">
        {timeHint || '\xa0'}
      </div>
    </>
  );

  return (
    <Wizard
      name={name}
      pages={[
        {
          id: 'complete-monitoring-page',
          title: Strings.completeMonitoring,
          content: pageContent,
          noScrollbar: true,
          emptyFieldsCount: validationErrors.filter(v => v.missing).length,
          canGoNext: validationErrors.length === 0 && !isSaving,
        },
      ]}
      onNextButtonHover={setHighlightInvalidFields}
      onSubmit={onSave}
      showPagesFilter={false}
    />
  );
}

CompleteMonitoringModal.propTypes = {
  name: PropTypes.string,
  data: PropTypes.shape({
    patientsCount: PropTypes.number,
    // receives the duration in seconds; may return a promise
    onConfirm: PropTypes.func,
  }),
  onCancel: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onCancel: () => dispatch(closeModal(ownProps.name || COMPLETE_MONITORING_MODAL_NAME)),
});

export default connect(null, mapDispatchToProps)(CompleteMonitoringModal);
